/**
 * Le calendrier du planning et la vue « Mois » (demande de Benjamin, 2026-10-30 : « d'ici un an, deux ans, cinq ans, je ne vais
 * pas remonter flèche par flèche »). Un petit calendrier s'ouvre sur le libellé de la période : l'année, ses douze mois, puis les
 * jours du mois avec leur numéro de semaine ; un clic ouvre la période dans le planning. La case « Aller au » prend une date tapée.
 *
 * Tout ici est pur (dates en texte `AAAA-MM-JJ`, calculs en UTC : aucun décalage d'heure d'été) et testé.
 */
import { ajouterJours, jourValide, lundiDe, numeroSemaineIso } from './planning';

export type VuePlanning = 'semaine' | 'mois';

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
] as const;

/** Les douze mois en abrégé, pour la grille des mois du calendrier. */
export const MOIS_CALENDRIER = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'] as const;

/** Les en-têtes des jours du calendrier (semaine ISO : lundi d'abord). */
export const JOURS_CALENDRIER = ['L', 'M', 'M', 'J', 'V', 'S', 'D'] as const;

function deux(n: number): string {
  return String(n).padStart(2, '0');
}

/** Le 1er du mois qui contient `jour`. */
export function premierDuMois(jour: string): string {
  return `${jour.slice(0, 7)}-01`;
}

/** Le 1er du mois décalé de `n` mois (négatif : en arrière). Passe les années sans erreur. */
export function ajouterMois(jour: string, n: number): string {
  const annee = Number(jour.slice(0, 4));
  const mois = Number(jour.slice(5, 7)) - 1 + Math.round(n);
  const a = annee + Math.floor(mois / 12);
  const m = ((mois % 12) + 12) % 12;
  return `${a}-${deux(m + 1)}-01`;
}

/** « octobre 2026 ». */
export function libelleMois(jour: string): string {
  return `${MOIS[Number(jour.slice(5, 7)) - 1] ?? ''} ${jour.slice(0, 4)}`;
}

/** Le jour à mettre dans l'adresse pour montrer `jour` dans la vue : le lundi de sa semaine, ou le 1er de son mois. */
export function debutDePeriode(vue: VuePlanning, jour: string): string {
  return vue === 'mois' ? premierDuMois(jour) : lundiDe(jour);
}

/** La période d'à côté (‹ ou ›) : une semaine, ou un mois. */
export function periodeVoisine(vue: VuePlanning, debut: string, sens: -1 | 1): string {
  return vue === 'mois' ? ajouterMois(debut, sens) : ajouterJours(lundiDe(debut), 7 * sens);
}

/** Une ligne du calendrier : le numéro de semaine ISO, son lundi, et ses sept jours (ceux d'un autre mois sont marqués). */
export interface SemaineCalendrier {
  readonly numero: number;
  readonly lundi: string;
  readonly jours: readonly { readonly jour: string; readonly dansLeMois: boolean }[];
}

/** Les semaines (lundi → dimanche) qui couvrent le mois de `jour` : 4 à 6 lignes, comme un calendrier mural. */
export function semainesDuMois(jour: string): SemaineCalendrier[] {
  const premier = premierDuMois(jour);
  const mois = premier.slice(0, 7);
  const semaines: SemaineCalendrier[] = [];
  for (let lundi = lundiDe(premier); lundi.slice(0, 7) <= mois; lundi = ajouterJours(lundi, 7)) {
    const jours = Array.from({ length: 7 }, (_, i) => {
      const j = ajouterJours(lundi, i);
      return { jour: j, dansLeMois: j.slice(0, 7) === mois };
    });
    semaines.push({ numero: numeroSemaineIso(lundi), lundi, jours });
  }
  return semaines;
}

/**
 * Une date tapée à la française (« 1/1/2026 », « 01/01/2026 », « 01-01-26 », « 01.01.2026 ») ou en ISO (« 2026-01-01 ») :
 * le jour en `AAAA-MM-JJ`, ou `null` si elle ne veut rien dire (31 février, texte). Une année à deux chiffres vaut 20xx.
 */
export function lireDateTapee(texte: string): string | null {
  const t = texte.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return jourValide(t) ? t : null;
  const m = /^(\d{1,2})[/.\- ](\d{1,2})[/.\- ](\d{2}|\d{4})$/.exec(t);
  if (!m) return null;
  const annee = (m[3] ?? '').length === 2 ? `20${m[3] ?? ''}` : (m[3] ?? '');
  const jour = `${annee}-${deux(Number(m[2]))}-${deux(Number(m[1]))}`;
  return jourValide(jour) ? jour : null;
}

/** La grille montre-t-elle un mois ? (une réponse d'avant le 2026-10-30 n'a pas de `vue` : c'est une semaine) */
export function estVueMois(periode: { readonly vue?: VuePlanning | undefined; readonly jours: readonly string[] }): boolean {
  return periode.vue === 'mois' || periode.jours.length > 7;
}

const INITIALES = ['D', 'L', 'M', 'M', 'J', 'V', 'S'] as const;

/** L'en-tête d'une colonne en vue Mois : l'initiale du jour et son numéro (« L », « 26 »). */
export function enteteJourMois(jour: string): { initiale: string; numero: string } {
  const d = new Date(`${jour}T00:00:00Z`);
  return { initiale: INITIALES[d.getUTCDay()] ?? '', numero: String(d.getUTCDate()) };
}

/** « octobre 2026 » → « Octobre 2026 » (un libellé en tête de ligne). */
export function majusculeInitiale(texte: string): string {
  return texte.charAt(0).toUpperCase() + texte.slice(1);
}

/**
 * Le jour qui guide une bascule Semaine / Mois : aujourd'hui s'il est dans la période affichée (du mois d'octobre on revient à la
 * semaine d'aujourd'hui, pas à la première semaine d'octobre), sinon le début de la période affichée.
 */
export function repereDeBascule(debut: string | null, fin: string | null, aujourdhui: string): string {
  if (debut === null) return aujourdhui;
  if (fin !== null && aujourdhui >= debut && aujourdhui <= fin) return aujourdhui;
  return debut;
}
